import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  IconButton,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import {
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon
} from "@material-ui/icons";
import { USER_SERVICES } from "Services";
import * as Utils from "Shared/Utils";
import { LEVELS } from "global-constants/constants";
import { TableListView } from "components";
import { Context } from "context";
import { ADD_NOTIFICATION } from "context/actions";
import { formatDate, getFileExtension, getImageProperties } from "utils";
import ConfirmModal from "Views/Common/ConfirmModal";
import styles from './LogoDetails.module.scss';

// The wallet card assets, as rows in this table rather than a section
// of their own. Sizes are the platforms' own minimums; the backend
// never upscales and names the failed rule in its 400.
const WALLET_ASSETS = [
  {
    key: 'wallet_logo',
    type: 'logo',
    category: 'Wallet artwork logo',
    hint: 'Square PNG, at least 660x660. Masked into a circle - keep artwork inside the middle 70%.',
  },
  {
    key: 'wallet_wide_logo',
    type: 'wide_logo',
    category: 'Wallet artwork wide logo',
    hint: 'PNG at 1280x400 (16:5), transparent background.',
  },
  {
    key: 'wallet_icon',
    type: 'icon',
    category: 'Wallet artwork icon',
    hint: 'Square PNG, at least 87x87. Use the mark alone, not the wordmark.',
  },
];

const LogoDetails = (props) => {
  const { state: { user } = {}, dispatch: globalDispatch } = useContext(Context);
  const {
    levelData: {
      partner_id,
      group_id,
      region_id,
      location_id
    } = {},
    settings: {
      setting_level,
      logo,
    } = {},
    acceptedFormats = ['.png'],
    // 1MB, the same figure as MAX_WALLET_ASSET_BYTES on the wallet
    // endpoint - the two are deliberately in step, so a file refused
    // here would have been refused there. The Logo row's legacy
    // endpoint enforces no size limit of its own, so for that row this
    // check is the only one.
    maxFileSize = 1 * 1024 * 1024,
    onUpdate
  } = props;

  // Wallet artwork belongs to the GROUP, and both endpoints are
  // SUBSCRIBILI_USERS only. Showing the rows anywhere else is not just
  // noise: the listing GET would 403, and httpService signs the user
  // out on a 403 - so a partner admin opening Group Info would be
  // logged out. An upload from a region or location page would also
  // write to the parent group, silently replacing artwork on every
  // card in it.
  const showWalletArtwork =
    Utils.checkIfInternalUser(user) && setting_level === LEVELS.GROUP;

  const [saveLoading, setSaveLoading] = useState(false);
  const [pdfUrls, setPdfUrls] = useState({});
  const [invoiceFile, setInvoiceFile] = useState();
  // Staged uploads, and what is already published, both keyed by the
  // asset type the endpoint takes.
  const [walletFiles, setWalletFiles] = useState({});
  const [walletAssets, setWalletAssets] = useState({});
  const [uploadedFile, setUploadedFile] = useState({});

  const [showPopup, setShowPopup] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const onDeleteAsset = (key) => {
    setDeleteLoading(true);
    try {
      // Add API to remove the key
      setShowPopup(false);
    }
    catch (err) {
      console.log(err);
    }
    setDeleteLoading(false);
  };

  const renderClearIcons = (key, rowId) => {
    return (
      <>
        <Tooltip arrow title="Clear">
          <IconButton className="mr-10" size="small" onClick={(evt) => {
              evt && evt.stopPropagation();
              clearUpload(key);
            }}
            data-testid={`clear-upload-button-${rowId}`}
          >
            <ClearIcon fontSize="inherit" className="theme_color_orange" />
          </IconButton>
        </Tooltip>
        <Tooltip arrow title="Save">
          <IconButton size="small" onClick={(evt) => {
              evt && evt.stopPropagation()
              saveLoading !== key && saveUpload(key);
            }}
            data-testid={`save-upload-button-${rowId}`}
          >
            {
              saveLoading === key
                ? <CircularProgress size={16} className="theme_color_orange" />
                : <SaveIcon fontSize="inherit" className="theme_color_orange" />
            }
          </IconButton>
        </Tooltip>
      </>
    );
  };
  

  const renderUploadIcons = (actionData, rowId) => {
    return (
      <Tooltip arrow title={actionData.uploadHint || "Upload logo in PNG format and resolution should be 200px X 64px"}>
        <IconButton className="mr-10" size="small" component="label" onClick={(evt) => evt && evt.stopPropagation()} data-testid={`upload-logo-button-${rowId}`}>
          <CloudUploadIcon fontSize="inherit" className="theme_color_orange" />
          <input
            type="file"
            hidden
            onChange={(e) => handleUpload(e, actionData)}
            onClick={(e) => {
              e.target.value = null;
            }}
            accept={acceptedFormats}
          />
        </IconButton>
      </Tooltip>
    )
  };

  const renderDeleteIcons = (key, rowId) => {
    return (
      <Tooltip arrow title="Delete">
        <IconButton size="small" onClick={(evt) => {
            evt && evt.stopPropagation()
            setShowPopup(key);
          }}
          data-testid={`delete-logo-button-${rowId}`}
        >
          <DeleteIcon className="theme_color_orange" fontSize="inherit" />
        </IconButton>
      </Tooltip>
    );
  };

  const commonCellProps = () => ({ className: styles.tableColumn });
  const tableColumns = [
    {
      name: "category",
      label: "Category",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ className: styles.categoryHeader }),
        setCellProps: commonCellProps
      },
    },
    {
      name: "fileName",
      label: "File Name",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ className: styles.nameHeader }),
        setCellProps: commonCellProps
      },
    },
    {
      name: "uploaded_on",
      label: "Uploaded on",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ className: styles.dateHeader }),
        setCellProps: commonCellProps
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        sort: false,
        setCellProps: commonCellProps,
        customBodyRender: (actionData, { rowIndex }) => {
          const { key, uploadFile, viewUrl, deletable } = actionData;

          return (
            <Grid container className={styles.buttonContainer} >
              {
                viewUrl ? (
                  <>
                    <Tooltip arrow title="View">
                      <IconButton className="mr-10" size="small" data-testid={`view-logo-button-${rowIndex}`}>
                        <VisibilityIcon fontSize="inherit" className="theme_color_orange" />
                      </IconButton>
                    </Tooltip>

                    {
                      uploadFile
                        ? renderClearIcons(key, rowIndex)
                        : (
                          <>
                            {renderUploadIcons(actionData, rowIndex)}
                            {deletable && renderDeleteIcons(key, rowIndex)}
                          </>
                        )
                    }
                  </>
                ) : (
                    <>
                      {renderUploadIcons(actionData, rowIndex)}
                      {uploadFile && renderClearIcons(key, rowIndex)}
                    </>
                  )
              }
            </Grid>
          )
        }
      },
    },
  ];

  const tableData = [
    {
      category: "Logo",
      fileName: logo?.name || '',
      uploaded_on: logo?.updated_on ? formatDate(logo?.updated_on * 1000) : '-',
      actions: {
        name: "Logo",
        key: "logo",
        uploadFile: invoiceFile,
        viewUrl: pdfUrls[logo?.key],
        deletable: true,
      }
    },
    ...(showWalletArtwork ? WALLET_ASSETS : []).map(({ key, type, category, hint }) => {
      const published = walletAssets[type];

      return {
        category,
        // The staged file while one is picked, otherwise whatever is
        // published. The published name is the object's own - the key
        // is stable and built from the asset type, so the original
        // upload's filename is not stored.
        fileName: walletFiles[type]?.name || published?.file_name || '',
        uploaded_on: published?.updated_at
          ? formatDate(published.updated_at)
          : '-',
        actions: {
          name: category,
          key,
          uploadFile: walletFiles[type],
          uploadHint: hint,
          // Versioned on the publish time. The key is stable, so a
          // re-upload otherwise shows the browser's cached copy until
          // max-age expires. The CDN ignores the query string.
          viewUrl: published?.public_url
            ? `${published.public_url}?v=${encodeURIComponent(published.updated_at || '')}`
            : undefined,
          // No delete endpoint for wallet artwork, and removing a row
          // would not unpublish the object anyway.
          deletable: false,
        },
      };
    }),
  ];

  const handleUpload = async (event, data) => {
    const file = event.target.files[0];
    const fileExtension = getFileExtension(file.name);
    const isWalletAsset = WALLET_ASSETS.some((a) => a.key === data.key);

    // The 64px rule is the GROUP LOGO's. Wallet artwork has its own
    // per-type minimums, which the backend validates.
    if (!isWalletAsset) {
      try {
        const { height } = await getImageProperties(file);
        if (height !== 64)
          throw new Error(
            'Please upload a logo with a height restricted to 64px',
          );
      } catch (error) {
        dispatchGlobalNotification('error', error);
        return;
      }
    }

    if (!acceptedFormats.includes(fileExtension)) {
      dispatchGlobalNotification('error', 'Unsupported file format');
      return;
    }

    if (file.size > maxFileSize) {
      // Derived, not written out - the hardcoded "1MB" outlived the
      // limit it described.
      dispatchGlobalNotification(
        'error',
        `File size should not be more than ${maxFileSize / (1024 * 1024)}MB`,
      );
      return;
    }

    setUploadedFile({
      ...uploadedFile,
      [data.key]: file,
    });

    onFileUpload(file, data.key);
  };

  const onFileUpload = (file, type) => {
    const walletType = WALLET_ASSETS.find((a) => a.key === type)?.type;
    if (walletType) {
      setWalletFiles((current) => ({ ...current, [walletType]: file }));
      return;
    }

    switch (type) {
      case "logo":
        setInvoiceFile(file);
        break;
      default:
        break;
    }
  };

  const clearUpload = (key) => {
    key === "logo" && setInvoiceFile();

    const walletType = WALLET_ASSETS.find((a) => a.key === key)?.type;
    if (walletType) {
      setWalletFiles((current) => ({ ...current, [walletType]: undefined }));
    }
  };

  const saveWalletAsset = async ({ key, type }) => {
    const file = walletFiles[type];
    if (!file) return;

    setSaveLoading(key);
    try {
      const fdata = new FormData();
      // The field name the endpoint reads; anything else is a 400.
      fdata.append('file', file);

      const response = await USER_SERVICES.uploadWalletBrandingAsset(
        group_id,
        type,
        fdata,
      );

      if (response?.type === 'success') {
        dispatchGlobalNotification('success', 'Wallet asset uploaded');
        clearUpload(key);
        // So the row shows the new name and date without a reload.
        await getWalletAssets();
      } else {
        // httpService RETURNS the error rather than throwing, so this
        // branch - not the catch - is what surfaces a rejected file.
        // The 400 names which rule failed, which is the whole point of
        // the response.
        dispatchGlobalNotification(
          'error',
          response?.message || 'Wallet asset upload failed',
        );
      }
    } catch (error) {
      dispatchGlobalNotification(
        'error',
        error?.message || 'Wallet asset upload failed',
      );
    } finally {
      setSaveLoading();
    }
  };

  const saveUpload = async (key) => {
    const walletAsset = WALLET_ASSETS.find((a) => a.key === key);
    if (walletAsset) {
      await saveWalletAsset(walletAsset);
      return;
    }

    setSaveLoading(key);
    try {
      const fdata = new FormData();
      const payload = {
        location_id,
        region_id,
        group_id,
        partner_id
      };

      const settingsData = {
        logo: ""
      };

      fdata.append(setting_level, JSON.stringify(payload));
      fdata.append("setting", JSON.stringify(settingsData));

      invoiceFile && fdata.append("logo", invoiceFile);

      const response = await USER_SERVICES.uploadFile(fdata, group_id);

      if (response?.type === 'success') {
        dispatchGlobalNotification('success', "File uploaded successfully");
        clearUpload("logo");
        onUpdate && onUpdate('Settings');
      }
      else {
        throw (response?.message || response?.error);
      }
    }
    catch (error) {
      let message = error;
      typeof error !== 'string' && (message = '');

      dispatchGlobalNotification("error", message || `Error while uploading the file`);
    }
    setSaveLoading(false);
  };

  const dispatchGlobalNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } }
    });

  const getPdfUrls = async () => {
    const getUrls = [];
    logo?.key && getUrls.push(logo?.key);

    if (getUrls.length) {
      const { data, type } = await USER_SERVICES.getAWSAccessURLs(getUrls);

      if (type === 'success') {
        setPdfUrls(data);
      }
    }
  };

  const onRowClick = (rowData, metaData) => {
    const { dataIndex = 0 } = metaData;
    // Each row carries its own url, so the Logo and the wallet assets
    // open the same way - the wallet ones are public CDN objects and
    // need no signing.
    const redirectUrl = tableData[dataIndex]?.actions?.viewUrl;

    redirectUrl && window.open(redirectUrl, "_blank", "noopener noreferrer");
  };

  const getWalletAssets = async () => {
    // Not merely pointless off the gate - a 403 here signs the user out.
    if (!showWalletArtwork || !group_id) return;

    const response = await USER_SERVICES.getWalletBrandingAssets(group_id);
    const assets = response?.data;
    if (!Array.isArray(assets)) return;

    setWalletAssets(
      assets.reduce(
        (acc, asset) => ({ ...acc, [asset.asset_type]: asset }),
        {},
      ),
    );
  };

  useEffect(() => {
    if (logo)
      getPdfUrls();
  }, [logo]);

  useEffect(() => {
    // void: an effect cannot await, and a floating promise fails
    // Sonar's reliability gate. httpService RESOLVES with an error
    // object rather than rejecting, so there is nothing to catch.
    void getWalletAssets();
  }, [group_id, showWalletArtwork]);

  return (
    <>
      <TableListView
        headerTitle="Logo"
        headerSection={true}
        tableData={tableData}
        tableOptions={{
          pagination: false,
          onRowClick
        }}
        tableColumns={tableColumns}
      />

      {
        showPopup &&
        <ConfirmModal
          isOpen={Boolean(showPopup)}
          closeModal={() => setShowPopup(false)}
          emitConfirmEvent={() => onDeleteAsset(showPopup)}
          isLoading={deleteLoading}
          options={{
            title: "Delete Logo",
            description: "Are you sure you want to delete?",
            confirmText: "Confirm",
            cancelText: "Cancel"
          }}
        />
      }
    </>
  );
};

export default LogoDetails;
